import type { HttpErrorResponse as OrderPayment } from '@angular/common/http';
import { ResponseCodes, putPaymentDetailsSuccess } from '@essent/new-customer';
import type { InnogyComponentRendering } from '@core/jss-models';
import type { ProgressiveFormStateImplementor } from '@innogy/progressive-ngrx-forms';
import {
  activateFormStep,
  createProgressiveFormGroupState,
  createProgressiveNgrxFormReducerWithFormStateUpdate,
  markAsyncTasksAsCompleted,
  onProgressiveNGRXFormsAction,
  SubmitFormStepAction,
} from '@innogy/progressive-ngrx-forms';
import {
  isFriendlyFormatIBAN,
  validateSequential,
} from '@innogy/utils-deprecated';
import type { Action } from '@ngrx/store';
import { on } from '@ngrx/store';
import { updateGroup } from 'ngrx-forms';
import { required } from 'ngrx-forms/validation';

import { addOrderRenderingAction } from '../order.actions';
import type { OrderState } from '../order.state';
import { ORDER_CROSS_SELL_FORM_ID, orderCrossSellEnabled } from '../cross-sell';
import { ORDER_ADDRESS_FORM_ID } from '../address';
import { ORDER_CUSTOMER_TYPE_FORM_ID } from '../customer-type';

export const ORDER_PAYMENT_FORM_ID: keyof OrderState = 'orderPaymentForm';
export const ibanFormControlId = `${ORDER_PAYMENT_FORM_ID}.iban`;

export interface OrderPaymentFormValues {
  iban: string;
}

export interface OrderPaymentState
  extends ProgressiveFormStateImplementor<OrderPaymentFormValues> {
  rendering: InnogyComponentRendering | undefined;
  previousStepId: keyof OrderState;
}

export const initialOrderPaymentProgressiveFormState =
  createProgressiveFormGroupState<OrderPaymentFormValues>(
    ORDER_PAYMENT_FORM_ID,
    {
      iban: '',
    }
  );

export const initialOrderPaymentState: OrderPaymentState = {
  progressiveForm: initialOrderPaymentProgressiveFormState,
  rendering: undefined,
  previousStepId: ORDER_ADDRESS_FORM_ID,
};

const validateForm = (state: OrderPaymentState) => {
  return updateGroup<OrderPaymentFormValues>({
    iban: validateSequential<string>(required, isFriendlyFormatIBAN),
  })(state.progressiveForm.formState);
};

/**
 * DEBT: because the cross sell component step is only injected by a feature flag, the stepId for activating this form step is dependant on that
 * @see https://jira.essent.nl/jira/browse/CTR-1481
 */
const stepActivationReducer = onProgressiveNGRXFormsAction(
  SubmitFormStepAction,
  (state: OrderPaymentState, action) => {
    if (
      action.stepId !== state.previousStepId ||
      state.progressiveForm.isSubmitted
    ) {
      return state;
    }
    return {
      ...state,
      progressiveForm: activateFormStep(state.progressiveForm),
    };
  }
);

const _reducer = createProgressiveNgrxFormReducerWithFormStateUpdate(
  initialOrderPaymentState,
  validateForm,
  on(orderCrossSellEnabled, (state, action) => {
    // if customerType is enabled, it should always be the previous step
    if (action.isCustomerTypeEnabled) {
      return { ...state, previousStepId: ORDER_CUSTOMER_TYPE_FORM_ID };
    }

    // If the order cross-sell step is enabled, this order step is activated after that step id instead
    if (action.enabled) {
      return { ...state, previousStepId: ORDER_CROSS_SELL_FORM_ID };
    } else {
      return { ...state, previousStepId: ORDER_ADDRESS_FORM_ID };
    }
  }),
  on(addOrderRenderingAction, (state, action) => ({
    ...state,
    rendering: action.rendering,
  })),
  stepActivationReducer,
  on(putPaymentDetailsSuccess, (state) => ({
    ...state,
    progressiveForm: markAsyncTasksAsCompleted(state.progressiveForm),
  }))
);

export function orderPaymentReducer(
  state: OrderPaymentState = initialOrderPaymentState,
  action: Action
): OrderPaymentState {
  return _reducer(state, action);
}

export function hasBlacklistedError(error?: OrderPayment): boolean {
  const messages: { code: string }[] = error?.error?.messages;

  if (messages) {
    for (const message of messages) {
      if (message.code === ResponseCodes.BLACKLISTED_IBAN) {
        return true;
      }
    }
  }

  return false;
}
